/**
 * Layer 3 — Capabilities. Reusable business features. They expose functionality
 * and carry NO licensing logic (editions/billing live in the resolver's floors).
 * Capabilities depend on platform services and on lower-level capabilities.
 */
import { MaturityLevel, defineCapability } from '@/lib/capabilities/contracts';

export const capabilities = {
  // ── Capture & media ──────────────────────────────────────────────────────
  'capabilities.photo_capture': defineCapability({
    key: 'capabilities.photo_capture',
    alwaysIncluded: 'Core: every inspection and job needs photos.',
    layer: 'capability',
    title: 'Photo capture',
    description: 'Capture photos as evidence across inspections, jobs and requests.',
    dependsOn: ['services.storage'],
    provides: ['evidence_media'],
    requires: ['file_storage'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Simple,
    betaFlag: false,
    internalOnly: false,
    tags: ['capture', 'write'],
    active: true,
  }),
  'capabilities.video_capture': defineCapability({
    key: 'capabilities.video_capture',
    layer: 'capability',
    title: 'Video capture',
    description: 'Attach short videos as evidence.',
    dependsOn: ['capabilities.photo_capture'],
    provides: ['evidence_video'],
    requires: ['evidence_media'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['capture', 'write'],
    active: true,
  }),
  'capabilities.drawing': defineCapability({
    key: 'capabilities.drawing',
    layer: 'capability',
    title: 'Drawing / markup',
    description: 'Annotate photos with drawings and callouts.',
    dependsOn: ['capabilities.photo_capture'],
    provides: ['media_markup'],
    requires: ['evidence_media'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['capture', 'write'],
    active: true,
  }),
  'capabilities.qr_codes': defineCapability({
    key: 'capabilities.qr_codes',
    layer: 'capability',
    title: 'QR codes',
    description: 'Generate and scan QR codes for asset identification.',
    dependsOn: ['services.search'],
    provides: ['qr'],
    requires: ['search'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['capture'],
    active: true,
  }),
  'capabilities.barcode_scan': defineCapability({
    key: 'capabilities.barcode_scan',
    layer: 'capability',
    title: 'Barcode scan',
    description: 'Scan barcodes to look up or capture asset identifiers.',
    dependsOn: ['services.mlkit'],
    provides: ['barcode'],
    requires: ['on_device_ml'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['capture'],
    active: true,
  }),
  'capabilities.voice_dictation': defineCapability({
    key: 'capabilities.voice_dictation',
    layer: 'capability',
    title: 'Voice dictation',
    description: 'Dictate notes and field content by voice.',
    dependsOn: ['services.auth'],
    provides: ['voice_input'],
    requires: ['identity'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['capture', 'write'],
    active: true,
  }),
  'capabilities.speech_recognition': defineCapability({
    key: 'capabilities.speech_recognition',
    alwaysIncluded: 'Part of Voice dictation; switch that instead.',
    layer: 'capability',
    title: 'Speech recognition',
    description: 'On-device speech-to-text powering voice dictation.',
    dependsOn: ['capabilities.voice_dictation'],
    provides: ['speech_to_text'],
    requires: ['voice_input'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['capture'],
    active: true,
  }),

  // ── Intelligence ─────────────────────────────────────────────────────────
  'capabilities.ml_kit': defineCapability({
    key: 'capabilities.ml_kit',
    alwaysIncluded: 'Part of photo capture: reads text and codes on the phone.',
    layer: 'capability',
    title: 'ML Kit',
    description: 'On-device machine-learning primitives (labels, barcodes, text).',
    dependsOn: ['services.mlkit', 'capabilities.photo_capture'],
    provides: ['ml_kit'],
    requires: ['on_device_ml', 'evidence_media'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Professional,
    betaFlag: false,
    internalOnly: false,
    tags: ['ai', 'on_device'],
    active: true,
  }),
  'capabilities.evidence_intelligence': defineCapability({
    key: 'capabilities.evidence_intelligence',
    layer: 'capability',
    title: 'Evidence intelligence',
    description: 'Analyse work evidence locally, escalating to AI only on demand.',
    dependsOn: ['services.evidence_intelligence', 'capabilities.photo_capture'],
    provides: ['evidence_analysis'],
    requires: ['evidence_intelligence', 'evidence_media'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Professional,
    betaFlag: false,
    internalOnly: false,
    tags: ['ai'],
    active: true,
  }),
  // WRX Vision. Two nodes so a workspace can keep on-device scanning while
  // switching cloud analysis off (a privacy control using the existing
  // workspace overrides); the vision-analyse-group edge function also reads
  // both nodes' disable overrides, so switching either off is enforced
  // server-side too. Generally available (2026-09-28): on unless a plan grant
  // (Admin -> Matrix) or an override switches it off; the server re-checks both.
  'capabilities.wrx_vision': defineCapability({
    key: 'capabilities.wrx_vision',
    layer: 'capability',
    title: 'WRX Vision',
    description: 'Scan an inspection group or item; on-device reading of meters, identifiers and barcodes, with suggestions reviewed before anything is saved.',
    dependsOn: ['capabilities.photo_capture'],
    provides: ['vision_scan'],
    requires: ['evidence_media'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Professional,
    betaFlag: false,
    internalOnly: false,
    tags: ['ai', 'on_device'],
    active: true,
  }),
  'capabilities.wrx_vision_cloud': defineCapability({
    key: 'capabilities.wrx_vision_cloud',
    layer: 'capability',
    title: 'WRX Vision cloud analysis',
    description: 'Cloud analysis of a Vision scan: condition, defects and not-observed checks against the checklist criteria.',
    dependsOn: ['capabilities.wrx_vision', 'services.ai_platform'],
    provides: ['vision_cloud_analysis'],
    requires: ['vision_scan', 'ai_inference'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Professional,
    betaFlag: false,
    internalOnly: false,
    tags: ['ai'],
    active: true,
    billingFeatureKey: 'ai_assistant',
    aiCapabilityKey: 'vision.analyse_group',
  }),
  // Specialist detector packs: domain expertise applied inside the ONE cloud
  // analysis (no extra charge). One node per pack so each can be switched off
  // on its own; vision-analyse-group reads their disable overrides, so a global
  // disable is the per-pack kill switch. See docs/vision/SPECIALIST_MODELS_ARCHITECTURE.md.
  'capabilities.wrx_vision_pack_fleet': defineCapability({
    key: 'capabilities.wrx_vision_pack_fleet',
    layer: 'capability',
    title: 'WRX Vision fleet pack',
    description: 'Vehicle damage checks: dents, scratches, glazing, lights, tyres and wheels.',
    dependsOn: ['capabilities.wrx_vision_cloud'],
    provides: ['vision_pack_fleet'],
    requires: ['vision_cloud_analysis'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Professional,
    betaFlag: false,
    internalOnly: false,
    tags: ['ai'],
    active: true,
  }),
  'capabilities.wrx_vision_pack_equipment': defineCapability({
    key: 'capabilities.wrx_vision_pack_equipment',
    layer: 'capability',
    title: 'WRX Vision equipment pack',
    description: 'Plant and equipment checks: guards, leaks, hoses, cables, corrosion, plates and meters.',
    dependsOn: ['capabilities.wrx_vision_cloud'],
    provides: ['vision_pack_equipment'],
    requires: ['vision_cloud_analysis'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Professional,
    betaFlag: false,
    internalOnly: false,
    tags: ['ai'],
    active: true,
  }),
  'capabilities.wrx_vision_pack_property': defineCapability({
    key: 'capabilities.wrx_vision_pack_property',
    layer: 'capability',
    title: 'WRX Vision property pack',
    description: 'Room and building checks: damp, mould-like growth, cracks, glazing, fittings and access.',
    dependsOn: ['capabilities.wrx_vision_cloud'],
    provides: ['vision_pack_property'],
    requires: ['vision_cloud_analysis'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Professional,
    betaFlag: false,
    internalOnly: false,
    tags: ['ai'],
    active: true,
  }),
  'capabilities.wrx_vision_pack_safety': defineCapability({
    key: 'capabilities.wrx_vision_pack_safety',
    layer: 'capability',
    title: 'WRX Vision safety equipment pack',
    description: 'Visual safety-equipment checks: extinguishers, alarms, signage and access. Never certifies.',
    dependsOn: ['capabilities.wrx_vision_cloud'],
    provides: ['vision_pack_safety'],
    requires: ['vision_cloud_analysis'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Professional,
    betaFlag: false,
    internalOnly: false,
    tags: ['ai'],
    active: true,
  }),
  // Vision improvement data: the photos already kept as evidence behind
  // reviewed suggestions may be used to train Vision. ON by default; the
  // workspace owner turns it off (a workspace 'disable' override). The database
  // (vision_training_share_enabled) is the source of truth, not this node's
  // resolution: the owner's switch reads get_vision_training_share.
  'capabilities.wrx_vision_training_share': defineCapability({
    key: 'capabilities.wrx_vision_training_share',
    layer: 'capability',
    title: 'WRX Vision improvement data',
    description: 'On by default: the evidence photos behind reviewed Vision suggestions are used to improve Vision. The workspace owner can turn it off.',
    dependsOn: ['capabilities.wrx_vision_cloud'],
    provides: ['vision_training_share'],
    requires: ['vision_cloud_analysis'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Professional,
    betaFlag: false,
    internalOnly: false,
    tags: ['ai', 'privacy'],
    active: true,
  }),
  // WRX Verified: evidence-based completion assurance on jobs. Deterministic
  // completeness (before/after/meter/document/check) needs only this node; the
  // cloud assessment additionally needs wrx_vision_cloud. Jobs complete exactly
  // as before when it is off. Which plans include it is set ONLY in Admin -> Matrix
  // (edition grants; every plan as of 2026-10-05), so minEdition is the lowest
  // edition and no copy names a plan for it; not a beta; the kill switch is a
  // global or workspace 'disable' override. See docs/verified/WRX_VERIFIED_ARCHITECTURE.md.
  'capabilities.wrx_verified': defineCapability({
    key: 'capabilities.wrx_verified',
    layer: 'capability',
    title: 'WRX Verified',
    description: 'Completion evidence on jobs: expected outcomes, before/after photos, and an evidence check the reviewer decides on.',
    dependsOn: ['capabilities.photo_capture'],
    provides: ['completion_evidence_assurance'],
    requires: ['evidence_media'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Professional,
    betaFlag: false,
    internalOnly: false,
    tags: ['ai', 'jobs'],
    active: true,
    billingFeatureKey: 'ai_assistant',
    aiCapabilityKey: 'vision.verify_completion',
  }),
  'capabilities.work_intelligence': defineCapability({
    key: 'capabilities.work_intelligence',
    layer: 'capability',
    title: 'Work intelligence',
    description: 'Shared work-evidence analysis facade across all workflows.',
    dependsOn: ['services.work_intelligence'],
    provides: ['work_analysis'],
    requires: ['work_intelligence'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Professional,
    betaFlag: false,
    internalOnly: false,
    tags: ['ai'],
    active: true,
  }),
  'capabilities.journey_intelligence': defineCapability({
    key: 'capabilities.journey_intelligence',
    alwaysIncluded: 'Not built yet, so there is nothing to switch off.',
    layer: 'capability',
    title: 'Journey intelligence',
    description: 'AI narration and insight over operational journeys.',
    dependsOn: ['services.journey_analytics_engine', 'services.ai_platform'],
    provides: ['journey_intelligence'],
    requires: ['journey_analytics', 'ai_inference'],
    minEdition: 'enterprise',
    maturityLevel: MaturityLevel.Enterprise,
    betaFlag: false,
    internalOnly: false,
    tags: ['ai', 'analytics'],
    active: true,
    billingFeatureKey: 'ai_assistant',
  }),
  'capabilities.journey_analytics': defineCapability({
    key: 'capabilities.journey_analytics',
    layer: 'capability',
    title: 'Journey analytics',
    description: 'Portfolio journey-flow dashboards and funnels.',
    dependsOn: ['services.journey_analytics_engine'],
    provides: ['journey_analytics_dashboards'],
    requires: ['journey_analytics'],
    minEdition: 'enterprise',
    maturityLevel: MaturityLevel.Enterprise,
    betaFlag: false,
    internalOnly: false,
    tags: ['analytics'],
    active: true,
  }),
  'capabilities.ai_summary': defineCapability({
    key: 'capabilities.ai_summary',
    layer: 'capability',
    title: 'AI summary',
    description: 'AI-generated summaries and drafts across surfaces.',
    dependsOn: ['services.ai_platform'],
    provides: ['ai_summary'],
    requires: ['ai_inference'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Professional,
    betaFlag: false,
    internalOnly: false,
    tags: ['ai'],
    active: true,
    billingFeatureKey: 'ai_assistant',
    aiCapabilityKey: 'inspection.generate_summary',
  }),
  'capabilities.ai_assistants': defineCapability({
    key: 'capabilities.ai_assistants',
    layer: 'capability',
    title: 'AI assistants',
    description: 'Interactive AI assistants and copilots.',
    dependsOn: ['services.ai_platform'],
    provides: ['ai_assistants'],
    requires: ['ai_inference'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Professional,
    betaFlag: false,
    internalOnly: false,
    tags: ['ai'],
    active: true,
    billingFeatureKey: 'ai_assistant',
  }),

  // ── Operations ─────────────────────────────────────────────────────────
  'capabilities.scheduling': defineCapability({
    key: 'capabilities.scheduling',
    layer: 'capability',
    title: 'Scheduling',
    description: 'Calendar planning and scheduled work.',
    dependsOn: ['services.notification_engine'],
    provides: ['scheduling'],
    requires: ['notifications'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['operations', 'write'],
    active: true,
  }),
  // WRX Work Planning: the planning calendar (people by time, capacity, travel,
  // parts and skills readiness), My Day, the Work map and Optimise. It is a
  // projection of Work, not a module: with it off, Work and today's calendar are
  // exactly as they were. Which plans include it is set ONLY in Admin -> Matrix
  // (edition grants: Business, 20261231600000); minEdition mirrors that as the
  // lock screen's fallback while the Matrix loads, and a test keeps them equal
  // (catalogMinEditionMatchesMatrix). See docs/planning/WORK_PLANNING_CURRENT_STATE.md.
  'capabilities.work_planning': defineCapability({
    key: 'capabilities.work_planning',
    layer: 'capability',
    title: 'WRX Work Planning',
    description: 'Plan who does what, when and in what order: people-by-time calendar, capacity, travel, parts and skills readiness, and suggested plans a person applies.',
    dependsOn: ['capabilities.scheduling'],
    provides: ['work_planning'],
    requires: ['scheduling'],
    minEdition: 'enterprise',
    maturityLevel: MaturityLevel.Professional,
    betaFlag: false,
    internalOnly: false,
    tags: ['operations', 'write'],
    active: true,
  }),
  'capabilities.recurring_work': defineCapability({
    key: 'capabilities.recurring_work',
    layer: 'capability',
    title: 'Recurring work',
    description: 'Recurring inspections and preventive maintenance schedules.',
    dependsOn: ['capabilities.scheduling', 'services.automation_engine'],
    provides: ['recurring_work'],
    requires: ['scheduling', 'automation'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Professional,
    betaFlag: false,
    internalOnly: false,
    tags: ['operations', 'automation', 'write'],
    active: true,
  }),
  'capabilities.approvals': defineCapability({
    key: 'capabilities.approvals',
    layer: 'capability',
    title: 'Approvals',
    description: 'Approval and triage flows for requests and completions.',
    dependsOn: ['services.notification_engine'],
    provides: ['approvals'],
    requires: ['notifications'],
    minEdition: 'professional',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['operations', 'write'],
    active: true,
  }),
  'capabilities.maintenance_programmes': defineCapability({
    key: 'capabilities.maintenance_programmes',
    layer: 'capability',
    title: 'Maintenance programmes',
    description: 'Multi-stage maintenance programmes with meter, calendar and sequenced stages.',
    dependsOn: ['capabilities.recurring_work'],
    provides: ['maintenance_programmes'],
    requires: ['recurring_work'],
    minEdition: 'professional',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['operations', 'automation', 'write'],
    active: true,
  }),
  'capabilities.schedule_exceptions': defineCapability({
    key: 'capabilities.schedule_exceptions',
    layer: 'capability',
    title: 'Schedule governance',
    description: 'Field requests to skip, reschedule or reassign scheduled work, and the decisions on them.',
    dependsOn: ['capabilities.approvals'],
    provides: ['schedule_exceptions'],
    requires: ['approvals'],
    // Free, deliberately. A workspace that cannot record WHY work did not
    // happen loses the record whatever it pays, and the alternative to a
    // governed skip is not "no skip" — it is an untracked one.
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['operations', 'write'],
    active: true,
  }),
  // ── Newer operational features (2026-09) ─────────────────────────────────
  // Each of these shipped without a key, so the admin edition matrix could not
  // include or exclude it from a package. The keys mirror the feature's entry
  // points in the app, which gate on them.
  'capabilities.condition_intelligence': defineCapability({
    key: 'capabilities.condition_intelligence',
    layer: 'capability',
    title: 'Condition intelligence',
    description: 'Rule-based asset condition, risk recommendations and technician fit on the asset and the scheduler.',
    dependsOn: ['services.reporting_engine'],
    provides: ['condition_intelligence'],
    requires: ['reporting'],
    minEdition: 'professional',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['operations'],
    active: true,
  }),
  'capabilities.location_tracking': defineCapability({
    key: 'capabilities.location_tracking',
    layer: 'capability',
    title: 'Location tracking',
    description: 'Asset location history and the claims raised by inspections, jobs and connected sources.',
    dependsOn: ['services.notification_engine'],
    provides: ['location_claims'],
    requires: ['notifications'],
    minEdition: 'professional',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['operations', 'write'],
    active: true,
  }),
  'capabilities.knowledge_base': defineCapability({
    key: 'capabilities.knowledge_base',
    layer: 'capability',
    title: 'Knowledge base',
    description: 'How-tos, FAQs, notes and files on an asset, the cross-asset Knowledge search and login-free share links.',
    dependsOn: ['services.storage', 'services.search'],
    provides: ['knowledge_base'],
    requires: ['file_storage', 'search'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Simple,
    betaFlag: false,
    internalOnly: false,
    tags: ['operations', 'write'],
    active: true,
  }),
  'capabilities.compliance_register': defineCapability({
    key: 'capabilities.compliance_register',
    layer: 'capability',
    title: 'Compliance register',
    description: 'Workspace-wide register of certificates, renewals and overdue evidence.',
    dependsOn: ['services.reporting_engine'],
    provides: ['compliance_register'],
    requires: ['reporting'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['operations'],
    active: true,
  }),
  'capabilities.industry_packs': defineCapability({
    key: 'capabilities.industry_packs',
    layer: 'capability',
    title: 'Industry packs',
    description: 'Install curated checklist and maintenance-plan packs, and per-asset pack suggestions.',
    dependsOn: ['capabilities.recurring_work'],
    provides: ['industry_packs'],
    requires: ['recurring_work'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Simple,
    betaFlag: false,
    internalOnly: false,
    tags: ['operations', 'write'],
    active: true,
  }),
  'capabilities.field_ai_assist': defineCapability({
    key: 'capabilities.field_ai_assist',
    layer: 'capability',
    title: 'Field AI assist',
    description: 'Dictate, improve and summarise on a text field at the point of effort.',
    dependsOn: ['services.ai_platform'],
    provides: ['field_ai_assist'],
    requires: ['ai_inference'],
    billingFeatureKey: 'ai_assistant',
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['ai'],
    active: true,
  }),
  'capabilities.quotes': defineCapability({
    key: 'capabilities.quotes',
    layer: 'capability',
    title: 'Quotes',
    description: 'Request, compare and select contractor quotes.',
    dependsOn: ['services.notification_engine'],
    provides: ['quotes'],
    requires: ['notifications'],
    minEdition: 'professional',
    maturityLevel: MaturityLevel.Professional,
    betaFlag: false,
    internalOnly: false,
    tags: ['operations', 'write'],
    active: true,
  }),
  'capabilities.offline_mode': defineCapability({
    key: 'capabilities.offline_mode',
    alwaysIncluded: 'Core: field work keeps working without signal.',
    layer: 'capability',
    title: 'Offline mode',
    description: 'Work offline with automatic sync on reconnect.',
    dependsOn: ['services.offline_sync'],
    provides: ['offline'],
    requires: ['offline_sync'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Simple,
    betaFlag: false,
    internalOnly: false,
    tags: ['operations'],
    active: true,
  }),
  'capabilities.notifications': defineCapability({
    key: 'capabilities.notifications',
    alwaysIncluded: 'Core: people must hear about work given to them.',
    layer: 'capability',
    title: 'Notifications',
    description: 'In-app, push and email notifications.',
    dependsOn: ['services.notification_engine'],
    provides: ['notification_delivery'],
    requires: ['notifications'],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Simple,
    betaFlag: false,
    internalOnly: false,
    tags: ['operations'],
    active: true,
  }),

  // ── Data & developer ─────────────────────────────────────────────────────
  'capabilities.csv_import': defineCapability({
    key: 'capabilities.csv_import',
    layer: 'capability',
    title: 'CSV import',
    description: 'Import contacts and assets from CSV.',
    dependsOn: ['services.integration_engine'],
    provides: ['csv_import'],
    requires: ['integration'],
    minEdition: 'professional',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['integration', 'write'],
    active: true,
  }),
  'capabilities.excel_import': defineCapability({
    key: 'capabilities.excel_import',
    layer: 'capability',
    title: 'Excel import',
    description: 'Import structured data from Excel workbooks.',
    dependsOn: ['services.integration_engine'],
    provides: ['excel_import'],
    requires: ['integration'],
    minEdition: 'professional',
    maturityLevel: MaturityLevel.Professional,
    betaFlag: false,
    internalOnly: false,
    tags: ['integration', 'write'],
    active: true,
  }),
  'capabilities.power_bi_export': defineCapability({
    key: 'capabilities.power_bi_export',
    layer: 'capability',
    title: 'Power BI export',
    description: 'Export analytics datasets for Power BI.',
    dependsOn: ['services.reporting_engine'],
    provides: ['power_bi_export'],
    requires: ['reporting'],
    minEdition: 'enterprise',
    maturityLevel: MaturityLevel.Enterprise,
    betaFlag: false,
    internalOnly: false,
    tags: ['integration', 'analytics'],
    active: true,
  }),
  'capabilities.power_automate': defineCapability({
    key: 'capabilities.power_automate',
    layer: 'capability',
    title: 'Power Automate',
    description: 'Trigger Power Automate flows from platform events.',
    dependsOn: ['services.integration_engine'],
    provides: ['power_automate'],
    requires: ['integration'],
    minEdition: 'enterprise',
    maturityLevel: MaturityLevel.Enterprise,
    betaFlag: false,
    internalOnly: false,
    tags: ['integration'],
    active: true,
    billingFeatureKey: 'premium_api',
  }),
  'capabilities.rest_api': defineCapability({
    key: 'capabilities.rest_api',
    layer: 'capability',
    title: 'REST API',
    description: 'First-party REST API access.',
    dependsOn: ['services.api_platform'],
    provides: ['rest_api'],
    requires: ['public_api'],
    minEdition: 'enterprise',
    maturityLevel: MaturityLevel.Enterprise,
    betaFlag: false,
    internalOnly: false,
    tags: ['developer'],
    active: true,
    billingFeatureKey: 'premium_api',
  }),
  'capabilities.graphql': defineCapability({
    key: 'capabilities.graphql',
    layer: 'capability',
    title: 'GraphQL',
    description: 'GraphQL API access.',
    dependsOn: ['services.graphql_platform'],
    provides: ['graphql_api'],
    requires: ['graphql'],
    minEdition: 'enterprise',
    maturityLevel: MaturityLevel.Enterprise,
    betaFlag: false,
    internalOnly: false,
    tags: ['developer'],
    active: true,
    billingFeatureKey: 'premium_api',
  }),
  'capabilities.webhooks': defineCapability({
    key: 'capabilities.webhooks',
    layer: 'capability',
    title: 'Webhooks',
    description: 'Subscribe to signed outbound webhooks.',
    dependsOn: ['services.webhook_platform'],
    provides: ['webhook_subscriptions'],
    requires: ['webhooks'],
    minEdition: 'enterprise',
    maturityLevel: MaturityLevel.Enterprise,
    betaFlag: false,
    internalOnly: false,
    tags: ['developer'],
    active: true,
    billingFeatureKey: 'premium_api',
  }),
  'capabilities.mcp': defineCapability({
    key: 'capabilities.mcp',
    layer: 'capability',
    title: 'MCP',
    description: 'Model Context Protocol access for agentic integrations.',
    dependsOn: ['services.mcp_platform'],
    provides: ['mcp_access'],
    requires: ['mcp'],
    minEdition: 'enterprise',
    maturityLevel: MaturityLevel.Enterprise,
    // Released on the Business plan 2026-10-04 (plan rules: Admin -> Matrix).
    betaFlag: false,
    internalOnly: false,
    tags: ['developer'],
    active: true,
    billingFeatureKey: 'premium_api',
  }),
  'capabilities.iot_connectors': defineCapability({
    key: 'capabilities.iot_connectors',
    layer: 'capability',
    title: 'IoT connectors',
    description: 'Ingest IoT signals to trigger work.',
    dependsOn: ['services.integration_engine'],
    provides: ['iot'],
    requires: ['integration'],
    minEdition: 'enterprise',
    maturityLevel: MaturityLevel.Enterprise,
    // Released on the Business plan 2026-10-04 (plan rules: Admin -> Matrix).
    betaFlag: false,
    internalOnly: false,
    tags: ['integration'],
    active: true,
    billingFeatureKey: 'iot_connectors',
  }),
  // AssetWRX Embedded (docs/embedded/architecture.md). A Business plan feature
  // (the Matrix disables it for free / starter / pro), not a beta. It is held
  // back at go-live by a global 'disable' row until the founder's live-tenant
  // acceptance; deleting that row releases it. It grants no data (same session,
  // same RLS, same screens) and does nothing without an admin-made integration.
  'capabilities.embed': defineCapability({
    key: 'capabilities.embed',
    layer: 'capability',
    title: 'Embedded AssetWRX',
    description: 'Run selected AssetWRX views (Work, Assets, inspections, jobs, reports) inside another portal, without the AssetWRX app chrome.',
    dependsOn: ['services.auth'],
    provides: ['embedded_presentation'],
    requires: [],
    minEdition: 'enterprise',
    maturityLevel: MaturityLevel.Enterprise,
    betaFlag: false,
    internalOnly: false,
    tags: ['integration'],
    active: true,
  }),
  // Embeds that CHANGE data (assets:write, inspections:write, jobs:write). An
  // integration may be configured with write scopes, but they only take effect
  // while the workspace also holds this (resolveEffectiveEmbedScopes), so a
  // read-only embed entitlement can never be widened by configuration.
  'capabilities.embed_interactive': defineCapability({
    key: 'capabilities.embed_interactive',
    layer: 'capability',
    title: 'Interactive embeds',
    description: 'Let embedded AssetWRX views update assets, perform inspections and change jobs, not only show them.',
    dependsOn: ['capabilities.embed'],
    provides: ['embedded_interaction'],
    requires: [],
    minEdition: 'enterprise',
    maturityLevel: MaturityLevel.Enterprise,
    betaFlag: false,
    internalOnly: false,
    tags: ['integration'],
    active: true,
  }),

  // ── Shipped after the flexible asset model (tracker 34.01, 2026-10-31) ──
  // Every row starts included on every plan (no grant row), so adding them
  // changed nothing for any workspace; the Matrix decides from here.
  'capabilities.group_rules': defineCapability({
    key: 'capabilities.group_rules',
    layer: 'capability',
    title: 'Group rules',
    description: 'Rules that schedule the same checklist on every matching asset, and keep it on assets added later.',
    dependsOn: ['capabilities.scheduling'],
    provides: ['group_rules'],
    requires: [],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['operations', 'write'],
    active: true,
  }),
  'capabilities.rounds': defineCapability({
    key: 'capabilities.rounds',
    layer: 'capability',
    title: 'Rounds',
    description: 'One job that walks a checklist across several assets in order.',
    dependsOn: [],
    provides: ['rounds'],
    requires: [],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['operations', 'write'],
    active: true,
  }),
  // Sub-jobs (journey review jm-22, Jay 2026-11-02). Starts on for every plan
  // (no grant rows), so nothing changes until Jay unticks a plan in the Matrix.
  'capabilities.sub_jobs': defineCapability({
    key: 'capabilities.sub_jobs',
    layer: 'capability',
    title: 'Sub-jobs',
    description: 'A job can hold sub-jobs, each with its own person or contractor, date, checks and costs; the parent waits for them.',
    dependsOn: ['modules.jobs'],
    provides: ['sub_jobs'],
    requires: [],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['operations', 'write'],
    active: true,
  }),
  // Contractors run inspections (journey review jm-09, Jay 2026-11-02). Starts
  // on wherever Contractors is on (no grant rows); Jay sets it per plan.
  'capabilities.contractor_inspections': defineCapability({
    key: 'capabilities.contractor_inspections',
    layer: 'capability',
    title: 'Inspections by contractors',
    description: 'Give an inspection to a contractor with an AssetWRX account; it appears in their work and they run it with the same checklist.',
    dependsOn: ['modules.contractors', 'modules.inspections'],
    provides: ['contractor_inspections'],
    requires: [],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['operations', 'write'],
    active: true,
  }),
  'capabilities.custom_asset_types': defineCapability({
    key: 'capabilities.custom_asset_types',
    layer: 'capability',
    title: 'Custom asset types and fields',
    description: 'Your own asset types with their own fields, beyond Place, Equipment, Vehicle and Other.',
    dependsOn: [],
    provides: ['custom_asset_types'],
    requires: [],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['assets', 'write'],
    active: true,
  }),
  'capabilities.bulk_asset_setup': defineCapability({
    key: 'capabilities.bulk_asset_setup',
    layer: 'capability',
    title: 'Bulk create and facility presets',
    description: 'Create many places or assets at once, or a whole building from a facility preset.',
    dependsOn: [],
    provides: ['bulk_asset_setup'],
    requires: [],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['assets', 'write'],
    active: true,
  }),
  'capabilities.contact_import': defineCapability({
    key: 'capabilities.contact_import',
    layer: 'capability',
    title: 'Contact import',
    description: 'Import contacts from the phone\'s address book or a file.',
    dependsOn: [],
    provides: ['contact_import'],
    requires: [],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['contacts', 'write'],
    active: true,
  }),
  'capabilities.ai_checklist_generation': defineCapability({
    key: 'capabilities.ai_checklist_generation',
    layer: 'capability',
    title: 'AI checklist generation',
    description: 'Draft a checklist with AI from a description, a photo or a document.',
    dependsOn: ['services.ai_platform'],
    provides: ['ai_checklist_generation'],
    requires: [],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['ai'],
    active: true,
    billingFeatureKey: 'ai_assistant',
    aiCapabilityKey: 'template.generate_draft',
  }),
  'capabilities.assistant_changes': defineCapability({
    key: 'capabilities.assistant_changes',
    layer: 'capability',
    title: 'Assistant can change records',
    description: 'The assistant prepares new and updated assets, sites, inspections, jobs and group rules for a person to confirm.',
    dependsOn: ['capabilities.ai_assistants'],
    provides: ['assistant_changes'],
    requires: [],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['ai', 'write'],
    active: true,
    billingFeatureKey: 'ai_assistant',
  }),
  'capabilities.assistant_planning': defineCapability({
    key: 'capabilities.assistant_planning',
    layer: 'capability',
    title: 'Assistant planning and optimisation',
    description: 'The assistant plans days, balances workload, suggests times and advises on maintenance programmes.',
    dependsOn: ['capabilities.ai_assistants'],
    provides: ['assistant_planning'],
    requires: [],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['ai'],
    active: true,
    billingFeatureKey: 'ai_assistant',
  }),
  'capabilities.assistant_insights': defineCapability({
    key: 'capabilities.assistant_insights',
    layer: 'capability',
    title: 'Assistant insights and briefings',
    description: 'The assistant explains trends, costs and causes, prepares reports and gives a weekly briefing.',
    dependsOn: ['capabilities.ai_assistants'],
    provides: ['assistant_insights'],
    requires: [],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['ai'],
    active: true,
    billingFeatureKey: 'ai_assistant',
  }),
  'capabilities.assistant_photo': defineCapability({
    key: 'capabilities.assistant_photo',
    layer: 'capability',
    title: 'Assistant photo questions',
    description: 'Ask the assistant about a photo you take or pick.',
    dependsOn: ['capabilities.ai_assistants'],
    provides: ['assistant_photo'],
    requires: [],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['ai'],
    active: true,
    billingFeatureKey: 'ai_assistant',
  }),
  'capabilities.assistant_voice': defineCapability({
    key: 'capabilities.assistant_voice',
    layer: 'capability',
    title: 'Assistant voice',
    description: 'Speak to the assistant and hear its replies (needs the latest app from the store).',
    dependsOn: ['capabilities.ai_assistants'],
    provides: ['assistant_voice'],
    requires: [],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['ai'],
    active: true,
    billingFeatureKey: 'ai_assistant',
  }),
  'capabilities.feature_requests': defineCapability({
    key: 'capabilities.feature_requests',
    layer: 'capability',
    title: 'Feature requests',
    description: 'Request a feature, follow your requests and vote on ideas we are considering.',
    dependsOn: [],
    provides: ['feature_requests'],
    requires: [],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['support'],
    active: true,
  }),

  // ── Asset 360 / Site 360 (tracker 39, Jay 2026-11-01 18:40: "make asset 360 and
  // site 360 a feature I can assign to editions"). Start included wherever Reports
  // is (no grant row), so nothing changes until Jay unticks a plan in the Matrix.
  'capabilities.asset_360': defineCapability({
    key: 'capabilities.asset_360',
    layer: 'capability',
    title: 'Asset 360',
    description: 'The Asset 360 report: verdict, scorecard, sections for the chosen reader, action plan and data quality, with an AI version on request.',
    dependsOn: ['modules.reports'],
    provides: ['asset_360'],
    requires: [],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['insights'],
    active: true,
  }),
  // ESG and sustainability add-on (tracker 44, Jay 2026-11-02). Switched off on
  // every plan by edition grants; buying the add-on turns it on for the
  // workspace (workspace_addons -> a workspace 'enable' override). "Included"
  // in the Matrix = remove a plan's disable grant.
  'capabilities.esg': defineCapability({
    key: 'capabilities.esg',
    layer: 'capability',
    title: 'ESG and sustainability',
    description: 'Energy, carbon, EPC ratings and F-gas refrigerant records for sites and assets, with a portfolio view and an ESG chapter in Site 360 and Asset 360. A paid add-on.',
    dependsOn: ['modules.assets'],
    provides: ['esg'],
    requires: [],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['insights'],
    active: true,
  }),
  'capabilities.esg_ai_writeup': defineCapability({
    key: 'capabilities.esg_ai_writeup',
    layer: 'capability',
    title: 'ESG AI write-up',
    description: 'A written ESG summary with suggested actions, made by AI from the ESG figures. Uses credits.',
    dependsOn: ['capabilities.esg', 'capabilities.ai_summary'],
    provides: ['esg_ai_writeup'],
    requires: [],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['insights', 'ai'],
    active: true,
  }),
  'capabilities.site_360': defineCapability({
    key: 'capabilities.site_360',
    layer: 'capability',
    title: 'Site 360',
    description: 'The Site 360 report: the whole site, its buildings and areas, compliance, risk, work and contractors, with an AI version on request.',
    dependsOn: ['modules.reports', 'modules.sites'],
    provides: ['site_360'],
    requires: [],
    minEdition: 'free',
    maturityLevel: MaturityLevel.Growing,
    betaFlag: false,
    internalOnly: false,
    tags: ['insights'],
    active: true,
  }),
} as const;
